import { overlayRoot } from '../../columnScale';
import { icon } from '../../icons';
import { registerEscape } from '../../keyboard';
import { toast } from '../../toast';
import type { ScreenCtx } from '../../types';
import {
  commitAndReload,
  exportSummary,
  incomingSummary,
  prepareImport,
  replaceConfirmText,
  saveFileName,
} from './saveImport';
import type { SaveData } from '../../../core/save';

/**
 * Fenêtre « Sauvegarde » du menu Réglages (v0.48.0, A96-3) : sortir la partie
 * du navigateur (changer de téléphone, la mettre à l'abri) et la remettre.
 * Deux volets :
 *
 *  - **Exporter** : « Copier le code de partie » (presse-papiers ; si le
 *    navigateur refuse, le texte est sélectionné dans la zone pour une copie
 *    à la main) et « Télécharger le fichier » (`diceordie-AAAA-MM-JJ.json`).
 *    Le code est la sauvegarde complète (`Game.exportSave()`), en JSON.
 *  - **Importer** : un code collé ou un fichier `.json`, puis « Importer » qui
 *    **demande confirmation** avant de remplacer la partie en cours. La partie
 *    importée n'est **pas** marquée debug (contrairement à l'import du panneau
 *    de debug) : c'est le chemin normal d'un joueur.
 *
 * Sélecteurs stables : `[data-role="save-transfer"]` (la fenêtre),
 * `[data-action="save-copy"]`, `[data-action="save-download"]`,
 * `[data-role="save-import-text"]`, `[data-role="save-import-file"]`,
 * `[data-action="save-import"]`, `[data-action="save-import-confirm"]`.
 */

type Pane = 'export' | 'import';

/** Copie `text` ; repli : sélectionne la zone et tente `execCommand`. */
async function copyText(text: string, area: HTMLTextAreaElement): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* refus de permission, contexte non sécurisé : repli ci-dessous */
  }
  area.focus();
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

/** Déclenche le téléchargement d'un fichier texte depuis le navigateur. */
function downloadText(name: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openSaveTransfer(ctx: ScreenCtx): void {
  const { game } = ctx;
  const now = new Date();
  const exportText = game.exportSave();

  const overlay = document.createElement('div');
  overlay.className = 'f-scrim';
  overlay.setAttribute('data-role', 'save-transfer');
  overlay.innerHTML = `
    <div class="f-modal save-modal" role="dialog" aria-modal="true" aria-labelledby="save-transfer-title">
      <div class="save-modal-head">
        <h2 class="f-modal-title" id="save-transfer-title">Sauvegarde</h2>
        <button type="button" class="f-ibtn" data-action="save-close" aria-label="Fermer">${icon('close')}</button>
      </div>
      <nav class="f-tabs" role="tablist" aria-label="Sauvegarde">
        <button type="button" class="f-tab" role="tab" data-pane="export" aria-selected="true">Exporter</button>
        <button type="button" class="f-tab" role="tab" data-pane="import" aria-selected="false">Importer</button>
      </nav>
      <section class="save-pane" data-role="save-pane-export" role="tabpanel">
        <p class="f-modal-text save-summary" data-role="save-summary"></p>
        <textarea class="f-input f-textarea save-text" data-role="save-export-text" rows="4" readonly spellcheck="false" aria-label="Code de partie"></textarea>
        <div class="f-modal-actions save-actions">
          <button type="button" class="f-btn f-btn--primary f-btn--sm" data-action="save-copy">Copier le code de partie</button>
          <button type="button" class="f-btn f-btn--sm" data-action="save-download">Télécharger le fichier</button>
        </div>
        <p class="save-note">Gardez ce code ou ce fichier : il contient toute votre partie.</p>
      </section>
      <section class="save-pane" data-role="save-pane-import" role="tabpanel" hidden>
        <label class="save-label" for="save-import-text">Collez votre code de partie</label>
        <textarea id="save-import-text" class="f-input f-textarea save-text" data-role="save-import-text" rows="4" spellcheck="false" placeholder="Collez ici le code de partie…"></textarea>
        <label class="f-btn f-btn--ghost f-btn--sm save-file">
          <span data-role="save-file-label">Choisir un fichier .json</span>
          <input type="file" class="sr-only" data-role="save-import-file" accept="application/json,.json">
        </label>
        <div class="f-modal-actions save-actions">
          <button type="button" class="f-btn f-btn--primary f-btn--sm" data-action="save-import">Importer</button>
        </div>
        <div class="save-confirm" data-role="save-confirm" role="alertdialog" aria-live="polite" hidden>
          <p class="f-modal-text save-confirm-text" data-role="save-confirm-text"></p>
          <p class="save-note" data-role="save-confirm-incoming"></p>
          <div class="f-modal-actions save-actions">
            <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="save-import-cancel">Annuler</button>
            <button type="button" class="f-btn f-btn--danger f-btn--sm" data-action="save-import-confirm">Remplacer la partie</button>
          </div>
        </div>
      </section>
    </div>
  `;

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
  const summary = q<HTMLElement>('[data-role="save-summary"]');
  const exportArea = q<HTMLTextAreaElement>('[data-role="save-export-text"]');
  const importArea = q<HTMLTextAreaElement>('[data-role="save-import-text"]');
  const fileInput = q<HTMLInputElement>('[data-role="save-import-file"]');
  const fileLabel = q<HTMLElement>('[data-role="save-file-label"]');
  const confirmBox = q<HTMLElement>('[data-role="save-confirm"]');
  const confirmText = q<HTMLElement>('[data-role="save-confirm-text"]');
  const confirmIncoming = q<HTMLElement>('[data-role="save-confirm-incoming"]');
  const importBtn = q<HTMLButtonElement>('[data-action="save-import"]');

  summary.textContent = exportSummary(now, game.state.level, game.state.gold);
  exportArea.value = exportText;

  let unregisterEscape = (): void => {};
  const close = (): void => {
    unregisterEscape();
    unregisterEscape = () => {};
    overlay.remove();
  };

  // ── Volets ────────────────────────────────────────────────────────────
  const tabs = Array.from(overlay.querySelectorAll<HTMLButtonElement>('[data-pane]'));
  const show = (pane: Pane): void => {
    for (const t of tabs) t.setAttribute('aria-selected', String(t.dataset.pane === pane));
    q<HTMLElement>('[data-role="save-pane-export"]').hidden = pane !== 'export';
    q<HTMLElement>('[data-role="save-pane-import"]').hidden = pane !== 'import';
  };
  for (const t of tabs) t.addEventListener('click', () => show(t.dataset.pane as Pane));

  // ── Exporter ──────────────────────────────────────────────────────────
  q('[data-action="save-copy"]').addEventListener('click', () => {
    void copyText(exportText, exportArea).then((ok) => {
      if (ok) {
        toast('Code de partie copié.', 2200, 'check');
      } else {
        exportArea.focus();
        exportArea.select();
        toast('Copie impossible ici : le texte est sélectionné, copiez-le à la main.', 3600);
      }
    });
  });
  q('[data-action="save-download"]').addEventListener('click', () => {
    downloadText(saveFileName(new Date()), exportText);
    toast('Fichier de partie téléchargé.', 2200, 'check');
  });

  // ── Importer ──────────────────────────────────────────────────────────
  /** Sauvegarde validée, en attente de la confirmation du joueur. */
  let pending: SaveData | null = null;
  const clearPending = (): void => {
    pending = null;
    confirmBox.hidden = true;
    importBtn.disabled = false;
  };
  // Toute modification du texte annule une confirmation en cours.
  importArea.addEventListener('input', clearPending);

  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    void f
      .text()
      .then((t) => {
        importArea.value = t;
        fileLabel.textContent = f.name;
        clearPending();
        toast('Fichier chargé : touchez « Importer ».', 2200, 'check');
      })
      .catch(() => toast('Import impossible : fichier illisible.'));
  });

  importBtn.addEventListener('click', () => {
    try {
      pending = prepareImport(importArea.value, { markDebug: false });
    } catch (err) {
      pending = null;
      toast(`Import impossible : ${(err as Error).message}`, 3200);
      return;
    }
    confirmText.textContent = replaceConfirmText(game.state.level, game.state.gold);
    confirmIncoming.textContent = incomingSummary(pending);
    confirmBox.hidden = false;
    importBtn.disabled = true;
    confirmBox.querySelector<HTMLButtonElement>('[data-action="save-import-cancel"]')?.focus();
  });
  q('[data-action="save-import-cancel"]').addEventListener('click', clearPending);
  q('[data-action="save-import-confirm"]').addEventListener('click', () => {
    if (!pending) return;
    if (!commitAndReload(pending)) {
      toast('Import impossible : le stockage du navigateur refuse l\'écriture.', 3600);
      return;
    }
    toast('Partie importée : rechargement…', 2200, 'check');
  });

  // ── Fermeture ─────────────────────────────────────────────────────────
  q('[data-action="save-close"]').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  overlayRoot().appendChild(overlay);
  unregisterEscape = registerEscape(overlay, close);
  q<HTMLButtonElement>('[data-action="save-close"]').focus();
}
